import { GridApi } from '@ag-grid-community/core';
import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { ApiService } from '@shared/services/api.service';
import {
  Document,
  EntityType,
  EventType,
  fetchPatientVisitSchedulesDataStreamQuery,
  GqlService,
  InvestigatorTransaction,
  InvoiceDataStream,
  PatientVisitSchedule,
  SniffDataStreamResponse,
  UserAuditLog,
} from '@shared/services/gql.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import {
  applyFilterTransformationsToFormGroup,
  DataStreamAnalytics,
  FetchDataStreamResponse,
  ServerSideDatasource,
  ServerSideFilterInfo,
} from '@shared/utils';
import { ValuesType } from '@shared/utils/utils';

export type PatientVisitDataSteam = ValuesType<fetchPatientVisitSchedulesDataStreamQuery['items']>;

@Injectable({
  providedIn: 'root',
})
export class DatasourceService {
  private readonly apiService = inject(ApiService);
  private readonly eventService = inject(EventService);
  private readonly gqlService = inject(GqlService);
  private readonly mainQuery = inject(MainQuery);

  /* DOCUMENT */
  private readonly _documentEndpoint = 'FetchDocuments';

  private readonly _documentDatasource = new ServerSideDatasource<Document>({
    apiService: this.apiService,
    endpoint: this._documentEndpoint,
    fetchFunction: (input) => firstValueFrom(this.gqlService.fetchDocumentsDataStream$(input)),
    gqlService: this.gqlService,
    mainQuery: this.mainQuery,
  });

  /* INVOICE LIBRARY */
  private readonly _invoiceLibraryEndpoint = 'FetchInvoiceLibrary';

  private readonly _invoiceLibraryDatasource = new ServerSideDatasource<InvoiceDataStream>({
    apiService: this.apiService,
    endpoint: this._invoiceLibraryEndpoint,
    fetchFunction: (input) => firstValueFrom(this.gqlService.fetchInvoiceLibraryDataStream$(input)),
    gqlService: this.gqlService,
    mainQuery: this.mainQuery,
  });

  /* PATIENT VISIT SCHEDULE */
  private readonly _patientVisitScheduleEndpoint = 'FetchPatientVisitSchedules';

  private readonly _patientVisitScheduleDatasource =
    new ServerSideDatasource<PatientVisitDataSteam>({
      apiService: this.apiService,
      endpoint: this._patientVisitScheduleEndpoint,
      fetchFunction: (input) =>
        firstValueFrom(this.gqlService.fetchPatientVisitSchedulesDataStream$(input)),
      gqlService: this.gqlService,
      mainQuery: this.mainQuery,
    });

  /* TRANSACTIONS */
  private readonly _transactionEndpoint = 'FetchPatientTransactions';

  private readonly _transactionDatasource = new ServerSideDatasource<InvestigatorTransaction>({
    apiService: this.apiService,
    endpoint: this._transactionEndpoint,
    fetchFunction: (input) =>
      firstValueFrom(this.gqlService.fetchPatientTransactionsDataStream$(input)) as Promise<
        GraphqlResponse<FetchDataStreamResponse<InvestigatorTransaction>>
      >,
    gqlService: this.gqlService,
    mainQuery: this.mainQuery,
  });

  /* USER AUDIT LOG */
  private readonly _userAuditLogEndpoint = 'FetchUserAuditLogs';

  private readonly _userAuditLogDatasource = new ServerSideDatasource<UserAuditLog>({
    apiService: this.apiService,
    endpoint: this._userAuditLogEndpoint,
    fetchFunction: (input) => firstValueFrom(this.gqlService.fetchUserAuditLogsDataStream$(input)),
    gqlService: this.gqlService,
    mainQuery: this.mainQuery,
  });

  /**
   * Prepares and returns an observable derived from the "formGroup.valueChanges" observable. It also returns the
   * current value of the form group. Both the current form value and the values that will be signalled by the
   * returned observable shall reflect the effect of the transformations applied for the corresponding input controls.
   */
  applyTransformations<T>(formGroup: FormGroup | unknown, filters: Array<ServerSideFilterInfo<T>>) {
    return applyFilterTransformationsToFormGroup(formGroup, filters);
  }

  get documentDatasource() {
    return this._documentDatasource;
  }

  /**
   * Retrieves the current sort model for the grid to which the gridApi is attached.
   */
  getCurrentSortModel<T>(
    gridApi: GridApi | null | undefined
  ): { column: keyof T; descending: boolean }[] {
    const result: Array<{ column: keyof T; descending: boolean }> = [];

    if (!(gridApi instanceof GridApi)) {
      return result;
    }

    const sortedColumnStates = gridApi
      .getColumnState()
      .filter((cs) => cs.sort && Number.isInteger(cs.sortIndex));

    sortedColumnStates.sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0));
    for (const columnState of sortedColumnStates) {
      const col = gridApi.getColumn(columnState.colId);

      if (col) {
        const columnState = col.getColDef();
        const columnName = columnState.colId || columnState.field;
        const column = columnState.colId ? gridApi.getColumn(columnState.colId) : '';

        if (!column) {
          console.warn(`Column ${columnName} is missing colId to apply sorting`);
        } else {
          result.push({
            column: (columnName || '') as keyof T,
            descending: column.isSortDescending(),
          });
        }
      }
    }

    return result;
  }

  get invoiceLibraryDatasource() {
    return this._invoiceLibraryDatasource;
  }

  private onRetrieveAnalytics(sniffDataStreamResponse: SniffDataStreamResponse) {
    const aggregation = JSON.parse(sniffDataStreamResponse.aggregation_json);

    if (aggregation && typeof aggregation === 'object') {
      return {
        aggregation,
        total_rows: sniffDataStreamResponse.total_rows,
      };
    } else {
      console.error('Invalid aggregation!');
    }
  }

  get patientVisitScheduleDatasource() {
    return this._patientVisitScheduleDatasource;
  }

  private async retrieveAnalytics<TData = Record<string, unknown>>(
    endpoint: string,
    input?: Record<string, unknown>
  ): Promise<DataStreamAnalytics<TData>> {
    try {
      if (!input || typeof input !== 'object') {
        input = {};
      }

      const trackerId = await this.eventService.triggerEvent({
        entity_id: '',
        entity_type: EntityType.INPUT,
        payload: JSON.stringify({
          input: JSON.stringify(input),
          operation: 'sniffDataStream',
          query_name: endpoint,
        }),
        type: EventType.ASYNC_DATA_STREAM_REQUEST,
      });
      const sniffDataStreamResponse =
        await this.eventService.getTrackerResponse<SniffDataStreamResponse>(trackerId || '');

      if (
        sniffDataStreamResponse?.aggregation_json &&
        Number.isInteger(sniffDataStreamResponse.total_rows) &&
        sniffDataStreamResponse.total_rows >= 0
      ) {
        const aggregation = JSON.parse(sniffDataStreamResponse.aggregation_json);

        if (aggregation && typeof aggregation === 'object') {
          return {
            aggregation,
            total_rows: sniffDataStreamResponse.total_rows,
          };
        } else {
          console.error('Invalid aggregation!');
        }
      } else {
        console.error(
          'Failed to retrieve analytics',
          endpoint,
          input,
          JSON.stringify(sniffDataStreamResponse, undefined, 2)
        );
      }
    } catch (err) {
      console.error('An error occurred while retrieving analytics', endpoint, input, err);
    }

    return {
      aggregation: {} as TData,
      total_rows: 0,
    };
  }

  retrieveInvoiceAnalytics<TInput = Record<string, unknown>>(input?: TInput) {
    return this.retrieveAnalytics<InvoiceDataStream>(this._invoiceLibraryEndpoint, input || {});
  }

  retrievePatientVisitScheduleAnalytics<TInput = Record<string, unknown>>(input?: TInput) {
    return this.retrieveAnalytics<PatientVisitSchedule>(
      this._patientVisitScheduleEndpoint,
      input || {}
    );
  }

  /* helpers */
  retrieveTransactionAnalytics<TInput = Record<string, unknown>>(input?: TInput) {
    return this.retrieveAnalytics<InvestigatorTransaction>(this._transactionEndpoint, input || {});
  }

  get transactionDatasource() {
    return this._transactionDatasource;
  }

  get userAuditLogDatasource() {
    return this._userAuditLogDatasource;
  }
}
